import { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument, degrees } from "pdf-lib";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface PDFPreviewProps {
  pdfFile: File;
  imageFile: File;
  rotation: number;
  scale: number;
}

export const PDFPreview = ({ pdfFile, imageFile, rotation, scale }: PDFPreviewProps) => {
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [pageWidth, setPageWidth] = useState<number>(600);

  useEffect(() => {
    const generatePreview = async () => {
      try {
        const pdfBytes = await pdfFile.arrayBuffer();
        const imageBytes = await imageFile.arrayBuffer();

        // Clone buffers before passing to libraries to avoid detachment
        const pdfBytesCopy = pdfBytes.slice(0);
        const imageBytesCopy = imageBytes.slice(0);

        const pdfDoc = await PDFDocument.load(pdfBytesCopy);
        const pngImage = await pdfDoc.embedPng(imageBytesCopy);

        const pages = pdfDoc.getPages();
        const firstPage = pages[0];
        const { width, height } = firstPage.getSize();

        const imageDims = pngImage.scale(scale);
        const marginRight = 21.6; // 0.3 inch = 21.6 points (72 points per inch)
        
        // Calculate rotated dimensions for proper positioning
        const radians = (rotation * Math.PI) / 180;
        const cos = Math.abs(Math.cos(radians));
        const sin = Math.abs(Math.sin(radians));
        const rotatedWidth = imageDims.width * cos + imageDims.height * sin;
        const rotatedHeight = imageDims.width * sin + imageDims.height * cos;
        
        // Position from right edge with margin
        const xPosition = width - rotatedWidth - marginRight;
        const yPosition = height * 0.15;

        firstPage.drawImage(pngImage, {
          x: xPosition,
          y: yPosition,
          width: imageDims.width,
          height: imageDims.height,
          rotate: degrees(rotation),
        });

        const modifiedPdfBytes = await pdfDoc.save();
        // pdf-lib returns a Uint8Array; pass directly into Blob parts
        const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        
        setPreviewUrl(url);
      } catch (error) {
        console.error("Error generating preview:", error);
      }
    };

    generatePreview();

    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [pdfFile, imageFile, rotation, scale]);

  return (
    <div className="bg-muted/30 rounded-lg p-4 flex items-center justify-center min-h-[400px]">
      {previewUrl ? (
        <Document
          file={previewUrl}
          loading={
            <div className="text-muted-foreground">Loading preview...</div>
          }
        >
          <Page
            pageNumber={1}
            width={pageWidth}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            className="shadow-lg"
          />
        </Document>
      ) : (
        <div className="text-muted-foreground">Generating preview...</div>
      )}
    </div>
  );
};
